import React from 'react';

/**
 * Historical Data Analysis Indicator
 * 
 * A specialized component for showing the enhanced onboarding analysis state
 * with animated progress indicators and professional messaging.
 */
const HistoricalDataAnalysisIndicator = ({ darkMode = false }) => {
  return (
    <div className={`
      relative p-4 rounded-lg border-l-4 border-purple-500 
      ${darkMode 
        ? 'bg-gradient-to-r from-purple-900/20 to-purple-800/10 border-purple-400' 
        : 'bg-gradient-to-r from-purple-50 to-purple-25 border-purple-500'
      }
    `}>
      {/* Header message */}
      <div className={`mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        <p className="text-sm font-medium">
          Perfect! Now let me pull everything together in your overview to give you my assessment.
        </p>
      </div>
      
      {/* Analysis status with animation */}
      <div className="flex items-center space-x-3">
        {/* Animated icon */}
        <div className="relative">
          <div className={`
            w-8 h-8 rounded-full border-2 border-purple-500 
            ${darkMode ? 'border-purple-400' : 'border-purple-500'}
            animate-spin
          `}>
            <div className={`
              absolute inset-2 rounded-full 
              ${darkMode ? 'bg-purple-400' : 'bg-purple-500'}
              animate-pulse
            `}></div>
          </div>
          
          {/* Pulse ring effect */}
          <div className={`
            absolute inset-0 rounded-full border-2 
            ${darkMode ? 'border-purple-400/30' : 'border-purple-500/30'}
            animate-ping
          `}></div>
        </div>
        
        {/* Status text with typewriter effect */}
        <div className="flex-1">
          <div className={`
            text-sm font-medium 
            ${darkMode ? 'text-purple-300' : 'text-purple-700'}
          `}>
            Analyzing your historical data
            <span className="inline-block animate-pulse">...</span>
          </div>
          <div className={`
            text-xs 
            ${darkMode ? 'text-gray-400' : 'text-gray-600'}
            mt-1
          `}>
            Processing 15 months of Search Console data
          </div>
        </div>
        
        {/* Progress dots */}
        <div className="flex space-x-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`
                w-2 h-2 rounded-full 
                ${darkMode ? 'bg-purple-400' : 'bg-purple-500'}
                animate-bounce
              `}
              style={{
                animationDelay: `${i * 0.2}s`,
                animationDuration: '1s'
              }}
            ></div>
          ))}
        </div>
      </div>
      
      {/* Shimmer effect overlay */}
      <div className={`
        absolute inset-0 rounded-lg overflow-hidden pointer-events-none
        ${darkMode ? 'bg-gradient-to-r from-transparent via-purple-400/5 to-transparent' : 'bg-gradient-to-r from-transparent via-purple-500/5 to-transparent'}
        animate-shimmer
      `}></div>
    </div>
  );
};

export default HistoricalDataAnalysisIndicator;
